import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { theme } from '../../styles/theme';

const VendorLookup = ({ 
  value, 
  onChange,
  department,
  error, 
  required = false,
  onVendorSelect = () => {},
  className = '' 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Load initial vendor if value is provided
    if (value) {
      fetchVendorById(value);
    } else {
      setSelectedVendor(null);
      setSearchTerm('');
    }
  }, [value]);

  useEffect(() => {
    // Handle click outside to close dropdown
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchVendorById = async (id) => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from('approved_vendors_with_details')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching vendor:', error);
        throw error;
      }

      if (data) {
        setSelectedVendor(data);
        setSearchTerm(`${data.supplier_code} - ${data.supplier_name}`);
      } else {
        // Clear selection if no data found
        setSelectedVendor(null);
        setSearchTerm('');
        onChange(null);
      }
    } catch (error) {
      console.error('Error fetching vendor:', error);
      // Clear selection on error
      setSelectedVendor(null);
      setSearchTerm('');
      onChange(null);
    }
  };

  const searchVendors = async (term) => {
    setLoading(true);
    try {
      let query = supabase
        .from('approved_vendors_with_details')
        .select('*')
        .or(`supplier_code.ilike.%${term}%,supplier_name.ilike.%${term}%`)
        .eq('status', 'Approved')
        .limit(10);

      // Filter by department if provided
      if (department) {
        query = query.eq('department_concerned', department);
      }

      const { data, error } = await query;

      if (error) throw error;
      setVendors(data || []);
      setShowDropdown(true);
    } catch (error) {
      console.error('Error searching vendors:', error);
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    // Clear selection if input is cleared
    if (!term) {
      setSelectedVendor(null);
      onChange(null);
      setVendors([]);
      setShowDropdown(false);
      return;
    }

    if (term.length >= 2) {
      searchVendors(term);
    } else {
      setVendors([]);
      setShowDropdown(false);
    }
  };

  const handleVendorSelect = (vendor) => {
    if (!vendor || !vendor.id) {
      setSelectedVendor(null);
      setSearchTerm('');
      onChange(null);
    } else {
      setSelectedVendor(vendor);
      setSearchTerm(`${vendor.supplier_code} - ${vendor.supplier_name}`);
      onChange(vendor.id);
      onVendorSelect(vendor);
    }
    setShowDropdown(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const isVendorValid = (vendor) => {
    if (!vendor) return false;
    
    const now = new Date();
    const validDate = vendor.valid_date ? new Date(vendor.valid_date) : null;
    const contractExpiry = vendor.contract_date_expires ? new Date(vendor.contract_date_expires) : null;

    return (!validDate || validDate > now) && (!contractExpiry || contractExpiry > now);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        External Vendor
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        className={`
          w-full px-4 py-2.5 rounded-lg
          border ${error ? 'border-red-500' : 'border-gray-300'}
          focus:outline-none focus:ring-2 focus:ring-opacity-50
          ${error ? 'focus:ring-red-500' : `focus:ring-[${theme.colors.primary.main}]`}
          ${error ? 'focus:border-red-500' : `focus:border-[${theme.colors.primary.main}]`}
        `}
        placeholder="Search by supplier code or name"
      />
      {loading && (
        <div className="absolute right-3 top-9">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
        </div>
      )}
      {showDropdown && vendors.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-auto">
          {vendors.map((vendor) => {
            const isValid = isVendorValid(vendor);
            return (
              <button
                key={vendor.id}
                onClick={() => handleVendorSelect(vendor)}
                className={`w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none ${!isValid ? 'opacity-50' : ''}`}
                disabled={!isValid}
              >
                <div className="font-medium">{vendor.supplier_code} - {vendor.supplier_name}</div>
                <div className="text-sm text-gray-600">
                  {vendor.station_iata_code && `Station: ${vendor.station_iata_code}`}
                  {vendor.city_name && ` - ${vendor.city_name}`}
                </div>
                <div className="text-xs text-gray-500">
                  Valid until: {formatDate(vendor.valid_date)}
                  {!isValid && <span className="text-red-500 ml-2">(Expired)</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      {selectedVendor && (
        <div className="mt-2 text-sm text-gray-600">
          <div>Contact: {selectedVendor.contact_person || 'N/A'}</div>
          <div>Email: {selectedVendor.email_address || 'N/A'}</div>
          <div>Contract: {selectedVendor.contract_name || 'N/A'}</div>
          <div>Contract Expires: {formatDate(selectedVendor.contract_date_expires)}</div>
        </div>
      )}
    </div>
  );
};

export default VendorLookup;
